'use client';
import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Props {
  selectedSite: string;
}

const ALL_COUNTS = {
  EXPIRED: 3,
  EXPIRING_SOON: 9,
  INSPECTION_DUE: 4,
  ALL_OK: 48,
};

const SITE_COUNTS: Record<string, typeof ALL_COUNTS> = {
  'Site Alpha': { EXPIRED: 1, EXPIRING_SOON: 3, INSPECTION_DUE: 1, ALL_OK: 13 },
  'Site Beta': { EXPIRED: 0, EXPIRING_SOON: 3, INSPECTION_DUE: 2, ALL_OK: 9 },
  'Warehouse B': { EXPIRED: 0, EXPIRING_SOON: 0, INSPECTION_DUE: 0, ALL_OK: 9 },
  'Site Gamma': { EXPIRED: 2, EXPIRING_SOON: 3, INSPECTION_DUE: 1, ALL_OK: 16 },
};

const TRAFFIC_LIGHTS = [
  {
    key: 'EXPIRED' as const,
    label: 'Expired',
    sublabel: 'Immediate action required',
    icon: 'XCircleIcon',
    bg: 'bg-red-50',
    border: 'border-red-200',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    valueColor: 'text-red-600',
    dot: 'bg-red-500',
    pulse: true,
    indicator: '#EF4444',
  },
  {
    key: 'EXPIRING_SOON' as const,
    label: 'Expiring Soon',
    sublabel: 'Within 30 days',
    icon: 'ClockIcon',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    valueColor: 'text-amber-600',
    dot: 'bg-amber-400',
    pulse: false,
    indicator: '#F59E0B',
  },
  {
    key: 'INSPECTION_DUE' as const,
    label: 'Inspection Due',
    sublabel: 'Overdue or due today',
    icon: 'CalendarDaysIcon',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    valueColor: 'text-orange-600',
    dot: 'bg-orange-500',
    pulse: false,
    indicator: '#F97316',
  },
  {
    key: 'ALL_OK' as const,
    label: 'All OK',
    sublabel: 'Fully compliant',
    icon: 'ShieldCheckIcon',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    valueColor: 'text-teal-700',
    dot: 'bg-teal-500',
    pulse: false,
    indicator: '#0D9488',
  },
];

export default function QHSETrafficLightSummary({ selectedSite }: Props) {
  const counts = selectedSite === 'All Sites' ? ALL_COUNTS : (SITE_COUNTS[selectedSite] ?? ALL_COUNTS);
  const total = counts.EXPIRED + counts.EXPIRING_SOON + counts.INSPECTION_DUE + counts.ALL_OK;

  return (
    <div className="space-y-3">
      {/* Traffic light cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {TRAFFIC_LIGHTS.map((tl) => {
          const value = counts[tl.key];
          const pct = total > 0 ? Math.round((value / total) * 100) : 0;
          return (
            <div
              key={tl.key}
              className={`relative rounded-2xl border ${tl.bg} ${tl.border} p-4 shadow-sm overflow-hidden`}
            >
              {/* Left accent bar */}
              <div
                className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
                style={{ background: tl.indicator }}
              />
              <div className="flex items-start justify-between mb-3 pl-1">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${tl.iconBg}`}>
                  <Icon name={tl.icon as Parameters<typeof Icon>[0]['name']} size={18} className={tl.iconColor} />
                </div>
                {tl.pulse && value > 0 && (
                  <span className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${tl.dot} animate-pulse`} />
                    <span className="text-[9px] font-700 text-red-600 uppercase tracking-wide">Critical</span>
                  </span>
                )}
              </div>
              <p className="text-[10px] font-700 uppercase tracking-widest text-gray-500 pl-1">{tl.label}</p>
              <p className={`text-3xl font-700 tabular-nums mt-0.5 pl-1 ${tl.valueColor}`}>{value}</p>
              <p className="text-[10px] text-gray-500 mt-0.5 pl-1">{tl.sublabel}</p>
              {/* Mini progress bar */}
              <div className="mt-3 h-1 bg-white/60 rounded-full overflow-hidden pl-1">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, background: tl.indicator }}
                />
              </div>
              <p className="text-[9px] text-gray-400 mt-1 pl-1">{pct}% of {total} items</p>
            </div>
          );
        })}
      </div>

      {/* Aggregate bar */}
      {total > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-600 text-gray-600">Overall Compliance Status</span>
            <span className="text-[11px] font-700 text-gray-700">{total} items tracked{selectedSite !== 'All Sites' ? ` · ${selectedSite}` : ''}</span>
          </div>
          <div className="flex h-2.5 rounded-full overflow-hidden gap-0.5">
            {TRAFFIC_LIGHTS.map((tl) => {
              const val = counts[tl.key];
              const w = total > 0 ? (val / total) * 100 : 0;
              return w > 0 ? (
                <div
                  key={tl.key}
                  className="h-full rounded-sm transition-all duration-500"
                  style={{ width: `${w}%`, background: tl.indicator }}
                  title={`${tl.label}: ${val}`}
                />
              ) : null;
            })}
          </div>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {TRAFFIC_LIGHTS.map((tl) => (
              <div key={tl.key} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: tl.indicator }} />
                <span className="text-[10px] text-gray-500">{tl.label}: <span className="font-600 text-gray-700">{counts[tl.key]}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
